import { useCallback, useEffect, useRef } from 'react';

import { Alert, Button, Card, Container } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import { useSuperState } from 'helpers/hooks';
import { useApp } from 'common/AppContext';

import { StepReviewPhases } from './components/StepReviewPhases';
import { StepCredentials } from './components/StepCredentials';
import { WizardProgress } from './components/WizardProgress';
import { StepTestPlan } from './components/StepTestPlan';
import { StepResults } from './components/StepResults';
import { Viewport } from './components/Viewport';
import { StepUrl } from './components/StepUrl';
import { StepRun } from './components/StepRun';

const TERMINAL_STATUSES = ['passed', 'completed', 'failed', 'cancelled', 'disconnected', 'ws_error'];

const initialState = {

    autoApprove: false,
    credentials: null,
    currentPhaseIndex: 0,
    currentStepIndex: 0,
    envContext: null,
    evidence: [],
    failureReason: null,
    freeform: false,
    hdBusy: false,
    hdEnabled: false,
    hdError: null,
    hdMode: null,
    hdSupported: typeof navigator !== 'undefined' && !!(navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia),
    pending: null,
    phases: null,
    planError: null,

    planning: false,
    runEndedAt: null,
    runPassed: null,

    // Step 5 has two phases: a share-required gate, then the actual run.
    // runStarted flips true after the user grants tab share + the run is
    // created. Until then, the run page shows the gate card.
    runStarted: false,
    runStartedAt: null,
    runSummary: null,
    runUuid: null,
    status: 'idle',
    steps: [],
    targetUrl: null,
    testPlan: '',
    url: '',

    // Wizard
    wizardStep: 1
};

function summarizeResult(result) {

    if (!result) return '';
    if (result.snapshot) return `${result.url || ''} (${result.snapshot.split('\n').length} nodes)`;
    return JSON.stringify(result).slice(0, 120);

}

export const Runner = () => {

    const { api } = useApp();
    const location = useLocation();

    const [state, setState] = useSuperState(initialState);

    // Deep-link prefill: pages like /azure-devops/:id push history with
    // `state: { prefill: { url, testPlan, freeform, appId } }`. We hydrate
    // the wizard from that payload exactly once on mount and jump to the
    // review step so the user can sanity-check / edit before starting.
    const prefilledRef = useRef(false);
    useEffect(() => {

        if (prefilledRef.current) return;
        const prefill = location && location.state && location.state.prefill;
        if (!prefill || !prefill.url || !prefill.testPlan) return;
        prefilledRef.current = true;

        setState({
            credentials: null,
            freeform: prefill.freeform !== false, // default to freeform — Claude already wrote the steps
            phases: null,
            testPlan: prefill.testPlan,
            url: prefill.url,
            wizardStep: 4
        });

    }, [location, setState]);
    const {
        autoApprove, credentials, currentPhaseIndex, currentStepIndex, envContext, evidence, failureReason, freeform, pending, phases, planError,
        planning, runEndedAt, runPassed, runStartedAt, runSummary, runUuid, status, steps,
        targetUrl, testPlan, url, wizardStep
    } = state;

    const wsRef = useRef(null);
    const viewportRef = useRef(null);
    const credentialsRef = useRef(null);
    const pendingHDStreamRef = useRef(null);

    useEffect(() => {

        credentialsRef.current = credentials;

    }, [credentials]);

    // Auto-advance to the results page when the run reaches a terminal status.
    // Fires only on the transition (running → terminal); subsequent renders
    // with the same terminal status do not re-trigger.
    const prevStatusRef = useRef(null);
    useEffect(() => {

        const prev = prevStatusRef.current;
        if (TERMINAL_STATUSES.includes(status) && !TERMINAL_STATUSES.includes(prev) && wizardStep === 5) {

            setState({ wizardStep: 6 });

        }
        prevStatusRef.current = status;

    }, [status, wizardStep, setState]);

    // Adopt the pre-acquired HD stream when the iframe ACTUALLY loads, not
    // just when wizardStep transitions to 5. There's a ~1-2s window between
    // those two events (api.post('/api/runs') has to resolve before the
    // targetUrl prop populates) during which the iframe is mounted but
    // empty. cropTo on an empty iframe hangs waiting for paintable content,
    // which is what produced the "Requesting share…" stuck state.
    const onViewportIframeLoad = useCallback(() => {

        const stream = pendingHDStreamRef.current;
        if (!stream || !viewportRef.current?.enableHighFidelityCapture) return;

        pendingHDStreamRef.current = null;
        viewportRef.current.enableHighFidelityCapture({ stream }).then(result => {

            if (result && result.ok) {

                setState({ hdBusy: false, hdEnabled: true, hdError: null, hdMode: result.mode });

            } else {

                // Adoption failed but the stream may still be live — keep
                // it stashed so a manual retry doesn't re-prompt the user.
                if (result && result.stream) pendingHDStreamRef.current = result.stream;
                setState({ hdBusy: false, hdEnabled: false, hdError: (result && result.error) || null, hdMode: null });

            }

        });

    }, [setState]);

    const sendWs = useCallback(payload => {

        const ws = wsRef.current;
        if (ws && ws.readyState === 1) ws.send(JSON.stringify(payload));

    }, []);

    const upsertStep = useCallback(step => {

        setState(prev => {

            const next = [...prev.steps];
            const idx = next.findIndex(s => s.id === step.id);
            if (idx === -1) next.push(step);
            else next[idx] = { ...next[idx], ...step };
            return { steps: next };

        });

    }, [setState]);

    const executeToolInIframe = useCallback(async msg => {

        const { phase_index, step_index, toolInput, toolName, toolUseId } = msg;

        try {

            const value = await viewportRef.current.executeTool(
                toolName, toolInput, credentialsRef.current
            );

            // Stash evidence in client state so the results page can render it.
            // The backend strips image_base64 before persistence, so this is the
            // only copy we keep client-side.
            if (toolName === 'capture_evidence' && value && value.image_base64) {

                setState(prev => ({
                    evidence: [
                        ...prev.evidence,
                        {
                            captured_at: Date.now(),
                            height: value.height,
                            image_base64: value.image_base64,
                            label: value.label || toolInput?.label || 'Evidence',
                            phase_index,
                            step_index,
                            url: value.url || null,
                            width: value.width
                        }
                    ]
                }));

            }

            sendWs({ action: 'tool_result', success: true, toolUseId, value });

        } catch (e) {

            sendWs({ action: 'tool_result', error: e.message, success: false, toolUseId });

        }

    }, [sendWs, setState]);

    const handleServerEvent = useCallback(async msg => {

        switch (msg.event) {

            case 'connected': setState({ status: 'connected' }); break;
            case 'run_started': setState({ runStartedAt: Date.now(), status: 'running' }); break;

            case 'env_context_loaded':
                setState({
                    envContext: { appName: msg.appName, envId: msg.envId, envName: msg.envName }
                });
                break;

            case 'step_proposed': {

                const step = {
                    id: msg.stepId,
                    index: msg.index,
                    phase_index: msg.phase_index,
                    reasoning: msg.reasoning,
                    status: 'proposed',
                    step_index: msg.step_index,
                    toolInput: msg.toolInput,
                    toolName: msg.toolName,
                    toolUseId: msg.toolUseId
                };
                upsertStep(step);
                setState({ pending: step });
                break;

            }

            case 'progress_update':
                setState({
                    currentPhaseIndex: msg.phase_index,
                    currentStepIndex: msg.step_index
                });
                break;

            case 'step_executing': upsertStep({ id: msg.stepId, status: 'executing' }); break;
            case 'step_completed':
                upsertStep({ id: msg.stepId, resultSummary: summarizeResult(msg.result), status: 'completed' });
                break;
            case 'step_failed': upsertStep({ id: msg.stepId, error: msg.error, status: 'failed' }); break;
            case 'step_skipped': upsertStep({ id: msg.stepId, status: 'skipped' }); break;

            case 'execute_tool': await executeToolInIframe(msg); break;

            case 'run_completed':
                setState({
                    pending: null,
                    runEndedAt: Date.now(),
                    runPassed: msg.passed != null ? !!msg.passed : null,
                    runSummary: msg.summary || null,
                    status: msg.passed === false ? 'failed' : msg.passed === true ? 'passed' : 'completed'
                });
                if (msg.summary) {

                    upsertStep({
                        id: `note-${Date.now()}`,
                        reasoning: msg.summary,
                        status: 'note',
                        toolName: 'finish'
                    });

                }
                break;

            case 'run_cancelled': setState({ pending: null, runEndedAt: Date.now(), status: 'cancelled' }); break;

            case 'run_failed':
                setState({
                    failureReason: msg.reason || 'orchestrator_error',
                    pending: null,
                    runEndedAt: Date.now(),
                    runPassed: false,
                    runSummary: msg.error || null,
                    status: 'failed'
                });
                upsertStep({ id: `error-${Date.now()}`, error: msg.error, status: 'failed', toolName: 'run_failed' });
                break;

            case 'run_note':
                upsertStep({ id: `note-${Date.now()}`, reasoning: msg.text, status: 'note', toolName: 'note' });
                break;

            default: break;

        }

    }, [setState, upsertStep, executeToolInIframe]);

    const generatePhases = useCallback(async (planText, notes = null) => {

        setState({ planError: null, planning: true });

        try {

            const data = await api.post('/api/runs/plan', { notes, testPlan: planText, url });
            setState({ phases: data.phases || [], planning: false, wizardStep: 4 });

        } catch (e) {

            setState({ planError: e?.err || e?.message || 'Failed to generate phases', planning: false });

        }

    }, [api, url, setState]);

    // Step 4 → 5 transition only — does NOT prompt for share. The actual
    // run kickoff is gated on the share button on step 5 (kickoffRun).
    const startRun = useCallback(() => {

        const phased = Array.isArray(phases) && phases.length > 0;
        if (!phased && !freeform) return;

        setState({
            currentPhaseIndex: 0,
            currentStepIndex: 0,
            evidence: [],
            hdBusy: false,
            hdEnabled: false,
            hdError: null,
            hdMode: null,
            pending: null,
            runEndedAt: null,
            runPassed: null,
            runStarted: false,
            runStartedAt: null,
            runSummary: null,
            status: 'idle',
            steps: [],
            wizardStep: 5
        });

    }, [phases, freeform, setState]);

    // Triggered by the share-required gate button on step 5. Prompts for tab
    // share (transient activation comes from this click), then opens the
    // run + WebSocket. If the user denies, surface an error and stay on the
    // gate so they can retry — share is required before the run can proceed.
    const kickoffRun = useCallback(async () => {

        if (!state.hdSupported) {

            setState({ hdError: 'This browser does not support tab share. Use Chrome or Edge.' });
            return;

        }

        setState({ hdBusy: true, hdError: null });

        let hdStream;
        try {

            hdStream = await navigator.mediaDevices.getDisplayMedia({
                audio: false,
                preferCurrentTab: true,
                selfBrowserSurface: 'include',
                video: { displaySurface: 'browser' }
            });

        } catch (e) {

            const message = e && e.name === 'NotAllowedError'
                ? 'Tab share is required to start a test. Click the button again and choose "This Tab".'
                : e?.message || 'Could not enable HD capture';
            setState({ hdBusy: false, hdError: message });
            return;

        }

        pendingHDStreamRef.current = hdStream;

        // Now we have the stream — kick off the actual run. Iframe mounts
        // when runStarted flips to true, then the iframe load event triggers
        // HD adoption against the live stream.
        setState({
            runStarted: true,
            status: 'creating'
        });

        const phased = Array.isArray(phases) && phases.length > 0;
        const body = phased
            ? { phases, url }
            : { steps: [testPlan], url };

        let res;
        try {

            res = await api.post('/api/runs', body);

        } catch (e) {

            setState({ hdBusy: false, status: 'failed' });
            alert(`Failed to create run: ${e.err || e.message}`);
            return;

        }

        setState({ runUuid: res.runUuid, status: 'connecting', targetUrl: url });

        const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const wsHost = process.env.REACT_APP_WS_HOST
            || (window.location.port === '3000' ? `${window.location.hostname}:8080` : window.location.host);

        const ws = new WebSocket(`${proto}//${wsHost}${res.wsUrl}`);
        wsRef.current = ws;

        ws.onopen = () => {

            sendWs({ action: 'start', hasCredentials: !!credentials });

        };

        ws.onmessage = e => {

            try {

                handleServerEvent(JSON.parse(e.data));

            } catch (err) {

                console.error('bad WS message', err);

            }

        };

        ws.onerror = () => setState(prev => ({
            runEndedAt: prev.runEndedAt || Date.now(),
            status: 'ws_error'
        }));
        ws.onclose = () => setState(prev => ({
            runEndedAt: prev.status === 'running' ? Date.now() : prev.runEndedAt,
            status: prev.status === 'running' ? 'disconnected' : prev.status
        }));

    }, [api, url, phases, freeform, testPlan, credentials, sendWs, handleServerEvent, setState, state.hdSupported]);

    const onApprove = useCallback(note => {

        sendWs({ action: 'approve', note: note || null });
        setState({ pending: null });

    }, [sendWs, setState]);

    const onSkip = useCallback(note => {

        sendWs({ action: 'skip', note: note || null });
        setState({ pending: null });

    }, [sendWs, setState]);

    const onCancel = useCallback(() => {

        if (!window.confirm('Cancel the current run?')) return;
        sendWs({ action: 'cancel' });
        setState({ pending: null });

    }, [sendWs, setState]);

    const setAutoApprove = useCallback(value => setState({ autoApprove: value }), [setState]);

    const onEnableHD = useCallback(async () => {

        if (!viewportRef.current?.enableHighFidelityCapture) return;

        setState({ hdBusy: true, hdError: null });

        // If a previous adoption attempt left us with a still-live stream
        // (e.g. timeout during auto-adopt) reuse it so we don't re-prompt.
        const existingStream = pendingHDStreamRef.current;
        const opts = existingStream && existingStream.active ? { stream: existingStream } : {};

        const result = await viewportRef.current.enableHighFidelityCapture(opts);
        if (result && result.ok) {

            pendingHDStreamRef.current = null;
            setState({ hdBusy: false, hdEnabled: true, hdError: null, hdMode: result.mode });

        } else {

            if (result && result.stream) pendingHDStreamRef.current = result.stream;
            const reason = result && result.reason;
            const message = reason === 'denied'
                ? 'Tab share denied.'
                : reason === 'unsupported'
                    ? 'Tab share not supported in this browser.'
                    : (result && result.error) || 'Couldn\'t enable HD capture.';
            setState({ hdBusy: false, hdEnabled: false, hdError: message, hdMode: null });

        }

    }, [setState]);

    const onDisableHD = useCallback(() => {

        viewportRef.current?.disableHighFidelityCapture?.();

        if (pendingHDStreamRef.current) {

            try {

                pendingHDStreamRef.current.getTracks().forEach(t => t.stop());

            } catch (_) { /* */ }
            pendingHDStreamRef.current = null;

        }
        setState({ hdEnabled: false, hdError: null, hdMode: null });

    }, [setState]);

    const onViewportError = useCallback(e => {

        // Handshake failure means the iframe loaded the page but the driver
        // script didn't run — most often because the script-tag URL on the
        // target site returned HTML instead of JS, or the target site has
        // a CSP that blocked it. Without the driver, no tool calls land.
        // Surface this as a hard run failure with the underlying error so
        // the QA analyst sees the reason instead of watching the orchestrator
        // burn through tool timeouts one at a time.
        const message = e?.message || 'Viewport error';
        console.warn('viewport:', e);
        setState(prev => prev.status === 'running' || prev.status === 'connected' || prev.status === 'connecting'
            ? {
                failureReason: 'viewport_handshake',
                runEndedAt: Date.now(),
                runSummary: message,
                status: 'failed'
            }
            : {});

    }, [setState]);

    const onHDChange = useCallback(status => {

        // Driven by the Viewport — fires when the browser ends the share
        // (e.g., user clicks "Stop sharing" in the share banner). We mirror
        // it back into Runner state so the sidebar UI reflects reality.
        setState({ hdEnabled: !!(status && status.enabled), hdMode: status && status.mode || null });

    }, [setState]);

    const onRunAgain = useCallback(() => {

        // Close the previous WS so we don't get stale messages.
        try {

            wsRef.current && wsRef.current.close();

        } catch (_) {}
        wsRef.current = null;

        startRun();

    }, [startRun]);

    const onRunWithChanges = useCallback(notes => {

        try {

            wsRef.current && wsRef.current.close();

        } catch (_) {}
        wsRef.current = null;

        // Move to the review-phases step before the API fires so the user
        // sees the regenerating spinner and any planError. Without this the
        // results page stays on screen during the silent fetch, and a failed
        // call leaves the user staring at the old results with no signal.
        setState({ wizardStep: 4 });
        generatePhases(testPlan, notes);

    }, [generatePhases, setState, testPlan]);

    const onStartNew = useCallback(() => {

        try {

            wsRef.current && wsRef.current.close();

        } catch (_) {}
        wsRef.current = null;

        setState(initialState);

    }, [setState]);

    // Step 5: gate first, then active run. The gate is required — the user
    // has to click Enable HD inside this page (transient activation comes
    // from that click). Only after share is granted do we mount Viewport
    // and create the run on the backend.
    if (wizardStep === 5) {

        if (!state.runStarted)
            return (
                <Container className="d-flex align-items-center justify-content-center" style={{ minHeight: '100%', padding: '3rem 1rem' }}>
                    <Card className="border-0 shadow-sm" style={{ maxWidth: 520, width: '100%' }}>
                        <Card.Body className="p-4">
                            <h4 className="mb-2">
                                Enable HD screenshots to start
                            </h4>
                            <p className="text-muted small mb-3">
                                Each test session needs to capture full-fidelity screenshots of the target page. Click the button below and choose
                                {' '}
                                <strong>
                                    This Tab
                                </strong>
                                {' '}
                                in the share dialog. The capture is scoped to this browser tab only — nothing else is recorded.
                            </p>
                            {state.hdError && (
                                <Alert className="small" variant="warning">
                                    {state.hdError}
                                </Alert>
                            )}
                            {state.hdSupported === false && (
                                <Alert className="small" variant="danger">
                                    This browser doesn&apos;t support tab capture. Switch to Chrome or Edge.
                                </Alert>
                            )}
                            <div className="d-flex gap-2 mt-3">
                                <Button
                                    onClick={() => setState({ wizardStep: 4 })}
                                    variant="outline-secondary"
                                >
                                    Back
                                </Button>
                                <Button
                                    className="flex-grow-1"
                                    disabled={state.hdBusy || state.hdSupported === false}
                                    onClick={kickoffRun}
                                    variant="primary"
                                >
                                    {state.hdBusy ? 'Awaiting share…' : 'Enable HD and start test'}
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>
                </Container>
            );

        return (
            <div style={{ display: 'grid', gridTemplateColumns: '380px 1fr', height: '100%', minHeight: 0 }}>

                <aside
                    className="d-flex flex-column gap-2 p-3 text-light"
                    data-bs-theme="dark"
                    style={{ background: '#1e1e1e', overflowY: 'auto' }}
                >
                    <StepRun
                        hd={{
                            busy: state.hdBusy,
                            enabled: state.hdEnabled,
                            error: state.hdError,
                            mode: state.hdMode,
                            supported: state.hdSupported
                        }}
                        autoApprove={autoApprove}
                        currentPhaseIndex={currentPhaseIndex}
                        currentStepIndex={currentStepIndex}
                        onApprove={onApprove}
                        onCancel={onCancel}
                        onDisableHD={onDisableHD}
                        onEnableHD={onEnableHD}
                        onSkip={onSkip}
                        phases={phases}
                        setAutoApprove={setAutoApprove}
                        state={{ pending, status, steps }}
                    />
                </aside>

                <main className="d-flex">
                    <Viewport
                        initialUrl={targetUrl}
                        onError={onViewportError}
                        onHDChange={onHDChange}
                        onIframeLoad={onViewportIframeLoad}
                        ref={viewportRef}
                        sessionKey={state.runUuid}
                    />
                </main>

            </div>
        );

    }

    // Step 6: dedicated results page.
    if (wizardStep === 6) {

        return (
            <StepResults
                currentPhaseIndex={currentPhaseIndex}
                currentStepIndex={currentStepIndex}
                envContext={envContext}
                evidence={evidence}
                failureReason={failureReason}
                freeform={freeform}
                onRunAgain={onRunAgain}
                onRunWithChanges={onRunWithChanges}
                onStartNew={onStartNew}
                phases={phases}
                runEndedAt={runEndedAt}
                runPassed={runPassed}
                runStartedAt={runStartedAt}
                runSummary={runSummary}
                runUuid={runUuid}
                status={status}
                steps={steps}
                testPlan={testPlan}
                url={url}
            />
        );

    }

    return (

        <Container className="py-3" style={{ maxWidth: 720 }}>

            <WizardProgress current={wizardStep} />

            {wizardStep === 1 && (
                <StepUrl
                    initialValue={url}
                    onNext={value => setState({ url: value, wizardStep: 2 })}
                />
            )}

            {wizardStep === 2 && (
                <StepCredentials
                    initialValue={credentials}
                    onBack={() => setState({ wizardStep: 1 })}
                    onNext={value => setState({ credentials: value, wizardStep: 3 })}
                />
            )}

            {wizardStep === 3 && (
                <StepTestPlan
                    onNext={({ freeform: nextFreeform, testPlan: value }) => {

                        if (nextFreeform) {

                            setState({ freeform: true, phases: null, testPlan: value, wizardStep: 4 });

                        } else {

                            setState({ freeform: false, testPlan: value });
                            generatePhases(value);

                        }

                    }}
                    busy={planning}
                    error={planError}
                    initialFreeform={freeform}
                    initialValue={testPlan}
                    onBack={() => setState({ wizardStep: 2 })}
                />
            )}

            {wizardStep === 4 && (
                <StepReviewPhases
                    autoApprove={autoApprove}
                    error={planError}
                    freeform={freeform}
                    onBack={() => setState({ wizardStep: 3 })}
                    onRegenerate={notes => generatePhases(testPlan, notes)}
                    onStart={startRun}
                    phases={phases}
                    regenerating={planning}
                    setAutoApprove={setAutoApprove}
                    testPlan={testPlan}
                />
            )}

        </Container>

    );

};
